/* CoolKicks Cart Additions v2: category rules + session-stable rotation. */
document.addEventListener('alpine:init', () => {
  const STORAGE_KEY = 'ck.cart-additions.v2';
  const MEMORY_KEY = '__ckCartAdditionsSessionV2';
  let instanceCount = 0;

  const textValue = (value) => String(
    value && typeof value === 'object' ? value.name || value.value || '' : value ?? ''
  );
  const typeKey = (value) => textValue(value).trim().toLowerCase();

  function apparelSize(value) {
    const key = textValue(value).toUpperCase().trim().replace(/[\s_-]+/g, '');
    const aliases = {
      XXS: 'XXS', '2XS': 'XXS', XXSMALL: 'XXS', EXTRAEXTRASMALL: 'XXS',
      XS: 'XS', XSMALL: 'XS', EXTRASMALL: 'XS',
      S: 'S', SMALL: 'S', M: 'M', MEDIUM: 'M', L: 'L', LARGE: 'L',
      XL: 'XL', XLARGE: 'XL', EXTRALARGE: 'XL',
      XXL: 'XXL', '2XL': 'XXL', XXLLARGE: 'XXL', XXLARGE: 'XXL',
      EXTRAEXTRALARGE: 'XXL',
      XXXL: 'XXXL', '3XL': 'XXXL', XXXLARGE: 'XXXL',
      '4XL': '4XL', XXXXL: '4XL', '5XL': '5XL', XXXXXL: '5XL',
    };
    return aliases[key] || '';
  }

  function sizeIndex(optionNames) {
    return (optionNames || []).findIndex((name) => /\bsize\b/i.test(textValue(name)));
  }

  function newSeed() {
    try {
      const values = new Uint32Array(4);
      window.crypto.getRandomValues(values);
      return Array.from(values, (value) => value.toString(16)).join('-');
    } catch (_) {
      return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
    }
  }

  function sessionState() {
    if (window[MEMORY_KEY]) return window[MEMORY_KEY];
    let saved;
    try { saved = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)); } catch (_) {}
    const state = {
      seed: typeof saved?.seed === 'string' && saved.seed ? saved.seed : newSeed(),
      variants: saved?.variants && typeof saved.variants === 'object' && !Array.isArray(saved.variants)
        ? saved.variants : {},
    };
    window[MEMORY_KEY] = state;
    saveSession(state);
    return state;
  }

  function saveSession(state) {
    // Storage can be blocked. The in-memory state still survives drawer refreshes.
    try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (_) {}
  }

  function randomRank(seed, pool, productId) {
    // Deterministic pseudo-random rank: new seed per tab session, same picks on
    // drawer re-renders. It does not use collection position or product sales.
    const input = seed + '|' + pool + '|' + productId;
    let hash = 2166136261;
    for (let i = 0; i < input.length; i += 1) {
      hash = Math.imul(hash ^ input.charCodeAt(i), 16777619);
    }
    hash ^= hash >>> 16;
    hash = Math.imul(hash, 0x7feb352d);
    hash ^= hash >>> 15;
    hash = Math.imul(hash, 0x846ca68b);
    return (hash ^ (hash >>> 16)) >>> 0;
  }

  function normalizeProduct(product) {
    const variants = (Array.isArray(product.variants) ? product.variants : [])
      .filter((variant) => variant.id && variant.available !== false && Number.isFinite(Number(variant.price)))
      .map((variant) => ({
        id: String(variant.id),
        title: textValue(variant.title),
        price: Number(variant.price),
        options: Array.isArray(variant.options)
          ? variant.options.map(textValue)
          : [variant.option1, variant.option2, variant.option3].filter((value) => value != null).map(textValue),
      }));
    return {
      id: String(product.id), title: textValue(product.title),
      titleKey: textValue(product.titleKey), type: textValue(product.type),
      url: textValue(product.url), image: textValue(product.image),
      options: Array.isArray(product.options) ? product.options.map(textValue) : [],
      hasOnlyDefaultVariant: product.hasOnlyDefaultVariant === true,
      variants,
    };
  }

  Alpine.data('ThemeModule_CartAdditions', () => {
    let payloadObserver = null;
    let lastPayload = null;
    return {
    adding: false,
    errorMessage: '',
    offers: [],
    currency: 'USD',
    locale: 'en-US',
    instanceId: ++instanceCount,

    init() {
      this.refreshFromPayload();
      // This theme uses Alpine.morph for cart updates. Morph can preserve this
      // component instead of running init again, so read changed server data.
      payloadObserver = new MutationObserver(() => this.refreshFromPayload());
      payloadObserver.observe(this.$root, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['data-cart-additions-payload'],
      });
    },

    destroy() {
      if (payloadObserver) payloadObserver.disconnect();
    },

    refreshFromPayload() {
      const payload = this.$root.querySelector('[data-cart-additions-payload]');
      const raw = payload ? payload.getAttribute('data-cart-additions-payload') : '';
      if (raw === lastPayload) return;
      lastPayload = raw;
      // Empty carts, or the original Liquid during a staged installation.
      if (!raw) { this.offers = []; return; }
      try {
        const data = JSON.parse(raw);
        this.currency = data.currency || 'USD';
        this.locale = data.locale || document.documentElement.lang || 'en-US';
        this.offers = this.chooseOffers(data);
      } catch (error) {
        // Recommendations must not prevent the main cart from working.
        this.offers = [];
        console.warn('CoolKicks Cart Additions could not load.', error);
      }
    },

    chooseOffers(data) {
      const cart = Array.isArray(data.cart) ? data.cart : [];
      if (!cart.length) return [];
      const maximum = cart.length > 1 ? 1 : 2;
      const cardsOnly = cart.every((item) => typeKey(item.type) === 'accessories');
      const shoeLine = cart.find((item) => !['accessories', 'apparel'].includes(typeKey(item.type)));
      const preferredShoeType = shoeLine ? typeKey(shoeLine.type) : '';
      const usedIds = new Set(cart.map((item) => String(item.id)));
      const usedTitles = new Set(cart.map((item) => textValue(item.titleKey)).filter(Boolean));
      const desiredSizes = new Set();
      const state = sessionState();

      cart.filter((item) => typeKey(item.type) === 'apparel').forEach((item) => {
        const index = sizeIndex(item.optionNames);
        const size = index >= 0 ? apparelSize((item.options || [])[index]) : '';
        if (size) desiredSizes.add(size);
      });

      const sizeMatch = (product, variant) => {
        const index = sizeIndex(product.options);
        return index >= 0 && desiredSizes.has(apparelSize(variant.options[index]));
      };
      const pools = {};
      for (const name of ['apparel', 'shoes', 'cards']) {
        pools[name] = (Array.isArray(data.pools?.[name]) ? data.pools[name] : [])
          .map(normalizeProduct)
          .filter((product) => product.variants.length > 0)
          .sort((a, b) => {
            if (name === 'apparel' && desiredSizes.size) {
              const aMatches = a.variants.some((variant) => sizeMatch(a, variant));
              const bMatches = b.variants.some((variant) => sizeMatch(b, variant));
              if (aMatches !== bMatches) return aMatches ? -1 : 1;
            }
            return randomRank(state.seed, name, a.id) - randomRank(state.seed, name, b.id)
              || a.id.localeCompare(b.id);
          });
      }

      const selected = [];
      const take = (pool, source, predicate = () => true) => {
        if (selected.length >= maximum) return false;
        const product = pools[pool].find((candidate) =>
          !usedIds.has(candidate.id)
          && (!candidate.titleKey || !usedTitles.has(candidate.titleKey))
          && predicate(candidate)
        );
        if (!product) return false;
        usedIds.add(product.id);
        if (product.titleKey) usedTitles.add(product.titleKey);
        const remembered = product.variants.find((variant) => variant.id === state.variants[product.id]);
        const matching = pool === 'apparel'
          ? product.variants.find((variant) => sizeMatch(product, variant)) : null;
        selected.push({
          ...product, source,
          selectedVariantId: (remembered || matching || product.variants[0]).id,
        });
        return true;
      };

      if (cardsOnly) {
        while (take('cards', 'trading-cards')) {}
      } else {
        take('apparel', 'apparel');
        if (preferredShoeType) {
          take('shoes', 'matching-footwear', (product) => typeKey(product.type) === preferredShoeType);
        }
        while (take('shoes', 'footwear')) {}
        while (take('apparel', 'apparel-fallback')) {}
      }
      return selected;
    },

    selectedVariant(offer) {
      return offer.variants.find((variant) => variant.id === String(offer.selectedVariantId))
        || offer.variants[0];
    },

    fillVariantOptions(select, offer) {
      // Native option elements avoid nested x-for scope loss in the theme's
      // Alpine 3.12 morph implementation. Text is always inserted safely.
      const selectedId = this.selectedVariant(offer).id;
      const options = offer.variants.map((variant) => {
        const option = document.createElement('option');
        option.value = variant.id;
        option.textContent = variant.title + ' \u2013 ' + this.formatPrice(variant.price);
        option.selected = variant.id === selectedId;
        return option;
      });
      select.replaceChildren(...options);
      select.value = selectedId;
    },

    rememberVariant(offer) {
      const state = sessionState();
      state.variants[offer.id] = this.selectedVariant(offer).id;
      // Bound the size of this session-only map.
      const keys = Object.keys(state.variants);
      keys.slice(0, Math.max(0, keys.length - 100)).forEach((key) => delete state.variants[key]);
      saveSession(state);
      this.errorMessage = '';
    },

    formatPrice(amount) {
      const value = Number(amount) / 100;
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency', currency: this.currency,
        }).format(value);
      } catch (_) {
        return this.currency + ' ' + value.toFixed(2);
      }
    },

    variantUrl(offer) {
      try {
        const url = new URL(offer.url, window.location.origin);
        url.searchParams.set('variant', this.selectedVariant(offer).id);
        return url.pathname + url.search + url.hash;
      } catch (_) {
        return offer.url;
      }
    },

    async addProduct(button) {
      if (this.adding) return;
      const card = button.closest('[data-cart-addition-card]');
      if (!card) return;
      const selector = card.querySelector('[data-cart-addition-variant]');
      const variantId = selector ? selector.value : button.dataset.variantId;
      const offer = this.offers.find((item) => item.id === String(card.dataset.productId));
      if (!variantId || (offer && !offer.variants.some((variant) => variant.id === String(variantId)))) {
        this.errorMessage = 'Please choose an available option.';
        return;
      }
      if (offer) {
        offer.selectedVariantId = String(variantId);
        this.rememberVariant(offer);
      }

      this.adding = true;
      this.errorMessage = '';
      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('sections', 'drawer-cart-items,drawer-cart-footer,cart-item-count');
        formData.append('sections_url', window.location.pathname);
        const config = fetchConfigDefaults('application/javascript');
        config.body = formData;
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const response = await fetch(theme.routes.cart_add_url, config);
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || window.theme.strings.cartError);
        }

        // Analytics must never interrupt a successful cart refresh.
        try {
          if (window.dataLayer && typeof window.dataLayer.push === 'function') {
            window.dataLayer.push({
              event: 'cross_sell_click', item_list_name: 'cart_additions',
              product_id: card.dataset.productId, product_name: card.dataset.productTitle,
              product_type: card.dataset.productType, offer_source: card.dataset.offerSource,
              variant_id: String(variantId), recommendation_method: 'session_rotation_v2',
            });
          }
        } catch (_) {}
        document.body.dispatchEvent(new CustomEvent('switch:cart:afteradditem', {
          bubbles: true, detail: { response: data },
        }));
      } catch (error) {
        this.errorMessage = error.message || window.theme?.strings?.cartError || 'Unable to add this item. Please try again.';
      } finally {
        this.adding = false;
      }
    },
    };
  });
});
